import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { performanceApi } from "./client";

export const performanceKeys = {
  hub: ["performance", "hub"] as const,
  kpi: (
    sortBy?: string,
    sortOrder?: string,
    limit?: number,
    offset?: number,
    q?: string,
  ) =>
    [
      "performance",
      "me",
      "kpi",
      sortBy,
      sortOrder,
      limit,
      offset,
      q,
    ] as const,
  kpiPeriodGroups: (limit?: number, offset?: number) =>
    ["performance", "me", "kpi", "period-groups", limit, offset] as const,
  kpiDetail: (assignmentId: string) =>
    ["performance", "me", "kpi", "detail", assignmentId] as const,
  tasks: (sortBy?: string, sortOrder?: string) =>
    ["performance", "me", "tasks", sortBy ?? "", sortOrder ?? ""] as const,
  projectMyTasks: (projectId: string) =>
    ["performance", "me", "project", projectId, "my-tasks"] as const,
};

export function usePerformanceHubQuery(options?: { enabled?: boolean }) {
  const { user } = useAuthState();
  const userId = user?.id;
  return useQuery({
    queryKey: performanceKeys.hub,
    queryFn: () => performanceApi.hub(),
    enabled: Boolean(userId) && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

export function usePerformanceMeKpiQuery(args: {
  sortBy: string;
  sortOrder: "asc" | "desc";
  limit: number;
  offset: number;
  q: string;
}) {
  const { user } = useAuthState();
  const userId = user?.id;
  return useQuery({
    queryKey: performanceKeys.kpi(
      args.sortBy,
      args.sortOrder,
      args.limit,
      args.offset,
      args.q,
    ),
    queryFn: () => performanceApi.meKpi({ ...args }),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });
}

export function usePerformanceMeKpiPeriodGroupsQuery(args: {
  limit: number;
  offset: number;
}) {
  const { user } = useAuthState();
  const userId = user?.id;
  return useQuery({
    queryKey: performanceKeys.kpiPeriodGroups(args.limit, args.offset),
    queryFn: () => performanceApi.meKpiPeriodGroups(args),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });
}

export function usePerformanceMeKpiDetailQuery(assignmentId: string) {
  const { user } = useAuthState();
  const userId = user?.id;
  return useQuery({
    queryKey: performanceKeys.kpiDetail(assignmentId),
    queryFn: () => performanceApi.meKpiDetail(assignmentId),
    enabled: Boolean(userId && assignmentId),
    staleTime: 60_000,
  });
}

export function usePerformanceMeTasksQuery(sort?: {
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}) {
  const { user } = useAuthState();
  const userId = user?.id;
  return useQuery({
    queryKey: performanceKeys.tasks(sort?.sortBy, sort?.sortOrder),
    queryFn: () => performanceApi.meTasks(sort),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });
}

export function usePerformanceProjectMyTasksQuery(projectId: string) {
  const { user } = useAuthState();
  const userId = user?.id;
  return useQuery({
    queryKey: performanceKeys.projectMyTasks(projectId),
    queryFn: () => performanceApi.meTasksInProject(projectId),
    enabled: Boolean(userId && projectId),
    staleTime: 60_000,
  });
}

export type UpdateAssignedTaskVariables =
  | { source: "meeting"; id: string; status: "pending" | "done" }
  | {
      source: "project";
      id: string;
      status: "pending" | "in_progress" | "done";
    };

/** ອັບເດດສະຖານະວຽກມອບໝາຍ (ກອງປະຊຸມ ຫຼື ໂຄງການ) — ສິດທີ່ເຊີເວີ: assignee ເທົ່ານັ້ນ */
export function useUpdateAssignedTaskStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: UpdateAssignedTaskVariables) =>
      v.source === "meeting"
        ? performanceApi.patchMeetingAssignedTask(v.id, { status: v.status })
        : performanceApi.patchProjectAssignedTask(v.id, { status: v.status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["performance"] });
    },
  });
}

/** ອັບເດດ % ຄວາມຄືບໜ້າວຽກໂຄງການ — assignee ເທົ່ານັ້ນ */
export function usePatchProjectTaskProgressMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; progressPercent: number | null }) =>
      performanceApi.patchProjectAssignedTask(v.id, {
        progressPercent: v.progressPercent,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["performance"] });
    },
  });
}
